<script setup>
// ---------------------------------------------------------------------------
// A ranking as sorted horizontal bars — the replacement for the prototype's
// pie charts, where thirteen sectors compete and several sit under one percent.
//
// Two measures share this component because they share the form: money by
// sector (rows, because every row is a contract with its own price) and cases
// by contracting authority (decisions, because that counts cases). Which one
// is drawn comes from `by`; the unit rule lives in cases.js.
// ---------------------------------------------------------------------------

import { computed } from 'vue'

import { sectorVolume, topAuthorities } from '../cases.js'
import { useCases } from '../useCases.js'

const props = defineProps({
    src: { type: String, required: true },
    by: { type: String, required: true },     // "sectors" | "authorities"
    keep: { type: String, default: '6' },
    // What is left over, said in the note rather than drawn: "{pocet}" for how
    // many of them there are, "{hodnota}" for how much they add up to.
    restLabel: { type: String, default: '' },
    baseLabel: { type: String, default: '' },
    errorText: { type: String, default: '' }
})

const { rows, visible, failed } = useCases(props.src)

const money = computed(() => props.by === 'sectors')

const data = computed(() => {
    if (!rows.value.length) return null

    return money.value
        ? sectorVolume(visible.value, Number(props.keep))
        : topAuthorities(visible.value, Number(props.keep))
})

// The tail is not a bar. Drawn, it was four times the longest real bar and the
// ranking underneath it turned into a rounding error — a chart about who comes
// up most often should not be dominated by everyone who does not.
const bars = computed(() => data.value.items.map((item) => ({ ...item, rest: false })))

// It cannot simply vanish either, so it is spelled out under the chart.
const rest = computed(() =>
    data.value.rest > 0
        ? props.restLabel
            .replace('{pocet}', data.value.restCount.toLocaleString('cs-CZ'))
            .replace('{hodnota}', format(data.value.rest))
        : ''
)

// The scale is every case, including the ones the tail holds and the chart does
// not draw. A bar is then a share of the whole caseload, which is what the row
// is asking: how much of everything does this authority account for. Measuring
// against the longest bar instead would fill the track for the busiest of them
// and say it is everything, when it is one case in thirty.
const whole = computed(
    () => bars.value.reduce((total, bar) => total + bar.value, 0) + (data.value.rest ?? 0) || 1
)

const billions = (value) =>
    `${(value / 1e9).toLocaleString('cs-CZ', { maximumFractionDigits: 1 })} mld. Kč`

const format = (value) => (money.value ? billions(value) : value.toLocaleString('cs-CZ'))

const base = computed(() => props.baseLabel)
</script>

<template>
    <p v-if="failed" class="chart__failed">{{ errorText }}</p>

    <div v-else-if="data" class="chart">
        <div class="bars">
            <div v-for="bar in bars" :key="bar.label" class="bars__row">
                <span class="bars__label">{{ bar.label }}</span>
                <span class="bars__track">
                    <span
                        class="bars__fill"
                        :class="{ 'bars__fill--rest': bar.rest }"
                        :style="{ width: `${(bar.value / whole) * 100}%` }"
                    ></span>
                    <span class="bars__value">{{ format(bar.value) }}</span>
                </span>
            </div>
        </div>

        <p class="note">{{ base }} {{ rest }}</p>
    </div>
</template>
